import type { FC, PropsWithChildren } from 'react';

import { CheckIcon } from '@/assets';
import type { BottomModalProps } from '@/features/popup/components/BottomModal';
import { BottomModal } from '@/features/popup/components/BottomModal';
import { isWeb } from '@/shared/utils/platform';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props = {
  isVisible: boolean;
  title?: string;
  onClose: () => void;
  onBackdropPress?: () => void;
  maxDynamicContentSizeRatio?: number;
  scrollComponent?: BottomModalProps['scrollComponent'];
  fillScrollContent?: boolean;
  stickyHeader?: boolean;
} & PropsWithChildren;

/**
 * Shared bottom-modal chrome for the post editor (title and confirm button).
 * Each editor section can configure its scroll component and height limit.
 */
export const PostEditorModalShell: FC<Props> = ({
  isVisible,
  title,
  onClose,
  onBackdropPress,
  maxDynamicContentSizeRatio,
  scrollComponent,
  fillScrollContent,
  stickyHeader,
  children
}) => {
  const theme = useAppTheme();

  return (
    <BottomModal
      isVisible={isVisible}
      title={title}
      titleAlign="left"
      onClose={onClose}
      showCloseButton={false}
      endButtons={[
        {
          icon: CheckIcon,
          shouldCloseWhenPressed: true,
          onPress: onClose,
          mode: 'contained',
          testID: 'post-editor-modal-confirm',
          theme: {
            colors: {
              surfaceVariant: theme.colors.error,
              primary: theme.colors.onPrimary
            }
          }
        }
      ]}
      keyboardShouldPersistTaps="handled"
      backdropPressBehavior="none"
      onBackdropPress={onBackdropPress}
      maxDynamicContentSizeRatio={
        isWeb ? undefined : maxDynamicContentSizeRatio
      }
      scrollComponent={isWeb ? undefined : scrollComponent}
      fillScrollContent={isWeb ? true : fillScrollContent}
      stickyHeader={!isWeb && stickyHeader}
    >
      {children}
    </BottomModal>
  );
};
